/**
 * 消息视图模型（任务 9d）：StoredMessage → 聊天窗口渲染模型。纯函数，无 DOM/状态。
 *
 * 口径来源（旧版 chat_screen.dart / im_msg_parser.dart 实读）：
 * - 连续 run 合并（_isSameSenderMessageGroup）：**仅群聊**（私聊每条自成 run）、
 *   同发送者、同日历日、双方都是气泡消息（system 提示条切断）。旧版还比
 *   vipLevel/premiumNo（变了就断 run，为换徽标）。21g-2 接入 VIP 徽标后**仍不比
 *   vipLevel**：服务端对一次拉取里同一发送者的所有行填的是同一个当前值
 *   （FillMsgMiniSenderVip 实时补齐），同 run 内不可能不一致；断 run 是旧版为了
 *   「同一 run 共用一次徽标渲染」的实现细节，新版逐行渲染不需要。premiumNo 未接入。
 * - run 内：首条显昵称（群聊对方消息）、末条显头像（旧版 run 级头像底对齐，
 *   平铺到虚拟列表即 runEnd 行渲染头像）、中间行收紧圆角（runStart/runEnd 标志）。
 * - 日期分隔条：首行或与上一时间线行跨日历日时插入（旧版按 run 算，但 run 不跨日，
 *   两种算法等价）。item 只带时间戳，文案 UI 层按 nowMs 算（formatDateSeparator）。
 * - 图片 envelope（_parseImage）：url|src|imageUrl / width|w / height|h / clock；
 *   非 JSON 整串当 URL。kind=sticker 的图片是自传表情包走图片通道的兼容形态，
 *   先按普通图片渲染（与旧版客户端一致），14.16 再还原成贴纸交互。
 * - 相册（14.1）：相邻同 album_id（ext，albumModel.parseAlbumId）同发送者的
 *   图片消息合并为一个 kind='album' 条目，网格渲染；条目 key 取首张保持稳定。
 * - signal 类行防御性跳过（messages-api 已过滤，WS 路径 apply.ts 不落库信令，
 *   这里兜底不信任上游）。
 *
 * 时间戳解析与 apply.ts:131 同口径：Date.parse(RFC3339)，失败=0（无分隔条、空时间）。
 *
 * 乐观消息（10b）：outbox 条目（pending/failed）合成本地行（id=0、uuid=clientId）
 * **追加在全部服务端行之后**（Telegram 同策——pending 常驻底部，期间到达的对方
 * 消息排在其上方，不按时间穿插）；key 绑 clientId（`msg:c:<clientId>`），重试/
 * 状态变化不换 key 气泡不重挂（legacy §5.4 echo 跳位根因）。run 逻辑照常参与
 * （与前一条自己的服务端行可并 run）。
 */

import type { StoredMessage } from '../worker/db/messages-store';
import type { OutboxEntry, OutboxStatus } from '../worker/chat/outbox';
import { msgTypeSpec, MsgType } from './msgTypes';
import { parseAlbumId } from './albumModel';
import { parseReplyExt, type ReplyRef } from './replyModel';
import { isSecretMessage } from './secretModel';
import { isAdminOnlyNote, parseTextEnvelope, messagePreview } from './preview';
import { formatBubbleTime, localDayOrdinal } from './formatTime';

// ---------------------------------------------------------------------------
// 内容模型（envelope → 渲染数据；text/image 先行，其余占位，14.x 逐项接管）
// ---------------------------------------------------------------------------

export type MessageContent =
  | {
      readonly kind: 'text';
      readonly text: string;
      readonly mentionType: number;
      readonly mentionTargets: readonly number[];
      readonly clock: number;
    }
  | {
      readonly kind: 'image';
      /** 相对路径或绝对 URL，展示时过 resolveMediaUrl */
      readonly url: string;
      /** 原图宽高（像素，0=未知——按默认比例占位） */
      readonly width: number;
      readonly height: number;
      readonly clock: number;
      /** 自传表情包兼容形态（kind=sticker），14.16 还原贴纸交互 */
      readonly stickerPayload: boolean;
      /**
       * 贴纸所属包 id（envelope pack_id 字符串化数字；0=缺失/非法）。
       * 14.17c：贴纸气泡点击按包 id 开整包预览（旧版 _handleStickerMessageTap）。
       */
      readonly stickerPackId: number;
    }
  | {
      /**
       * 相册（14.1）：相邻同 album_id 的图片消息合并为一个网格气泡。
       * 条目级 id/uuid/key 取首张（key 稳定不随追加变）；每张的消息身份在
       * images 里，菜单/预览/删除按张操作（albumTileToItem 合成单张视图）。
       */
      readonly kind: 'album';
      readonly albumId: string;
      readonly images: readonly AlbumImageVM[];
    }
  | {
      /** 语音（14.2a）：url 相对路径，播放控制在 voicePlayer（shell 层） */
      readonly kind: 'voice';
      readonly url: string;
      /** envelope dur_ms（毫秒，0=未知——播放拿到媒体时长后以媒体为准） */
      readonly durationMs: number;
      readonly size: number;
      readonly codec: string;
      readonly clock: number;
    }
  | {
      /** 文件（14.3）：通用卡片，点击=下载/另存为（PC download / H5·standalone share） */
      readonly kind: 'file';
      readonly url: string;
      /** 原始文件名（含扩展名，保存时沿用；空=未知走兜底名） */
      readonly fileName: string;
      readonly size: number;
      readonly mime: string;
      readonly clock: number;
    }
  | {
      /**
       * 视频（14.3）：与文件同构的通用卡片（旧版无缩略图播放器，envelope 的
       * cover 字段实际恒空），点击打开播放浮层（原生 <video controls>）。
       */
      readonly kind: 'video';
      readonly url: string;
      readonly fileName: string;
      readonly size: number;
      readonly mime: string;
      readonly durationMs: number;
      readonly clock: number;
    }
  | {
      /**
       * 名片（14.4）：头像+名字+ID 卡片。点击→好友详情/加好友归 14.21，
       * 详情页接入前卡片不可点（旧版 onContactCardTap 为空时同样只展示）。
       */
      readonly kind: 'contact';
      /** 被分享人的 uid（0=envelope 缺失，详情页入口不可用） */
      readonly userId: number;
      readonly name: string;
      /** 展示用 ID（旧版发送侧写的是 UserId 字符串，别名表见解析函数） */
      readonly username: string;
      readonly avatar: string;
      readonly clock: number;
    }
  | {
      /** 未接管类型的占位气泡：label 来自 messagePreview，空则兜底文案 */
      readonly kind: 'placeholder';
      readonly label: string;
    };

/** 相册里单张图的消息身份 + 展示数据 */
export interface AlbumImageVM {
  /** 该张自己的行 key（msg:<id> / msg:c:<clientId>），菜单定位用 */
  readonly key: string;
  readonly id: number;
  readonly uuid: string;
  readonly url: string;
  readonly width: number;
  readonly height: number;
  /** 该张自己的置顶标记（14.10；置顶按消息 id 逐张，不随相册整册） */
  readonly pinned: boolean;
  readonly sendState?: OutboxStatus;
}

function strField(r: Record<string, unknown>, keys: readonly string[]): string {
  for (const k of keys) {
    const v = r[k];
    if (typeof v === 'string' && v !== '') return v;
  }
  return '';
}

function numField(r: Record<string, unknown>, keys: readonly string[]): number {
  for (const k of keys) {
    const v = r[k];
    if (typeof v === 'number' && Number.isFinite(v) && v !== 0) return v;
  }
  return 0;
}

/** 图片 envelope 解析（旧版 _parseImage 同口径）。非 JSON / 解析失败整串当 URL。 */
export function parseImageEnvelope(msg: string): Extract<MessageContent, { kind: 'image' }> {
  let raw: unknown = null;
  if (msg.startsWith('{')) {
    try {
      raw = JSON.parse(msg);
    } catch {
      raw = null;
    }
  }
  if (typeof raw !== 'object' || raw === null) {
    return {
      kind: 'image',
      url: msg,
      width: 0,
      height: 0,
      clock: 0,
      stickerPayload: false,
      stickerPackId: 0,
    };
  }
  const r = raw as Record<string, unknown>;
  // pack_id 线上形态是字符串化数字（buildStickerEnvelope/旧版同口径），容忍裸数字
  const packIdRaw = r['pack_id'];
  const packId =
    typeof packIdRaw === 'string' || typeof packIdRaw === 'number'
      ? Math.trunc(Number(packIdRaw)) || 0
      : 0;
  return {
    kind: 'image',
    url: strField(r, ['url', 'src', 'imageUrl']),
    width: numField(r, ['width', 'w']),
    height: numField(r, ['height', 'h']),
    clock: numField(r, ['clock', 'Clock']),
    stickerPayload: r['kind'] === 'sticker',
    stickerPackId: packId,
  };
}

/**
 * 语音 envelope 解析（旧版 _parseVoice 同口径，字段别名一致）：
 * `{"url","dur_ms","size","codec","clock"}`（protocol §5.4）。非 JSON 整串当 URL。
 */
export function parseVoiceEnvelope(msg: string): Extract<MessageContent, { kind: 'voice' }> {
  let raw: unknown = null;
  if (msg.startsWith('{')) {
    try {
      raw = JSON.parse(msg);
    } catch {
      raw = null;
    }
  }
  if (typeof raw !== 'object' || raw === null) {
    return { kind: 'voice', url: msg, durationMs: 0, size: 0, codec: '', clock: 0 };
  }
  const r = raw as Record<string, unknown>;
  return {
    kind: 'voice',
    url: strField(r, ['url', 'src', 'voiceUrl']),
    durationMs: numField(r, ['dur_ms', 'durMs', 'durationMs']),
    size: numField(r, ['size']),
    codec: strField(r, ['codec', 'mime']),
    clock: numField(r, ['clock', 'Clock']),
  };
}

/**
 * 文件 envelope 解析（旧版 _parseFile 同口径，字段别名一致）：
 * `{"url","fileName","name","size","mime","clock"}`（protocol §5.4，fileName/name
 * 冗余双写）。非 JSON 整串当 URL。
 */
export function parseFileEnvelope(msg: string): Extract<MessageContent, { kind: 'file' }> {
  let raw: unknown = null;
  if (msg.startsWith('{')) {
    try {
      raw = JSON.parse(msg);
    } catch {
      raw = null;
    }
  }
  if (typeof raw !== 'object' || raw === null) {
    return { kind: 'file', url: msg, fileName: '', size: 0, mime: '', clock: 0 };
  }
  const r = raw as Record<string, unknown>;
  return {
    kind: 'file',
    url: strField(r, ['url', 'src', 'fileUrl']),
    fileName: strField(r, ['filename', 'fileName', 'name']),
    size: numField(r, ['size']),
    mime: strField(r, ['mime', 'mimeType', 'type']),
    clock: numField(r, ['clock', 'Clock']),
  };
}

/**
 * 视频 envelope 解析（旧版 _parseVideo 同口径）：
 * `{"url","fileName","name","dur_ms","size","mime","cover","width","height","clock"}`
 * （protocol §5.4）。cover/width/height 不取——旧版渲染为通用文件卡片无缩略图，
 * 字段实际恒空，新版同级（任务清单口径）。非 JSON 整串当 URL。
 */
export function parseVideoEnvelope(msg: string): Extract<MessageContent, { kind: 'video' }> {
  let raw: unknown = null;
  if (msg.startsWith('{')) {
    try {
      raw = JSON.parse(msg);
    } catch {
      raw = null;
    }
  }
  if (typeof raw !== 'object' || raw === null) {
    return { kind: 'video', url: msg, fileName: '', size: 0, mime: '', durationMs: 0, clock: 0 };
  }
  const r = raw as Record<string, unknown>;
  return {
    kind: 'video',
    url: strField(r, ['url', 'src', 'videoUrl']),
    fileName: strField(r, ['filename', 'fileName', 'name']),
    size: numField(r, ['size']),
    mime: strField(r, ['mime', 'mimeType', 'type']),
    durationMs: numField(r, ['dur_ms', 'durMs', 'durationMs']),
    clock: numField(r, ['clock', 'Clock']),
  };
}

/**
 * 名片 envelope 解析（旧版 _parseContactCard 同口径，字段别名一致）：
 * `{"UserId","Name","Avatar","Username","clock"}`（protocol §5.4）。
 * 旧版发送侧 UserId 写的是**字符串**（sendContactCard 实锤），userId 需兼容
 * 数字/数字串两种形态；username 别名表含 userId 系（字符串值才取，同旧版
 * _readString 口径——发送侧 UserId 是字符串所以兜底成立）。
 * 非 JSON / 解析失败返回全空卡片（渲染为「联系人名片」兜底，与旧版同级）。
 */
export function parseContactCardEnvelope(
  msg: string,
): Extract<MessageContent, { kind: 'contact' }> {
  let raw: unknown = null;
  if (msg.startsWith('{')) {
    try {
      raw = JSON.parse(msg);
    } catch {
      raw = null;
    }
  }
  if (typeof raw !== 'object' || raw === null) {
    return { kind: 'contact', userId: 0, name: '', username: '', avatar: '', clock: 0 };
  }
  const r = raw as Record<string, unknown>;
  const idKeys = ['userId', 'UserId', 'uid', 'id'] as const;
  let userId = numField(r, idKeys);
  if (userId === 0) {
    const s = strField(r, idKeys);
    const n = Number.parseInt(s, 10);
    if (Number.isFinite(n) && n > 0) userId = n;
  }
  return {
    kind: 'contact',
    userId,
    name: strField(r, ['name', 'Name', 'nickname', 'Nickname']),
    username: strField(r, ['username', 'Username', 'userNo', 'UserNo', ...idKeys]),
    avatar: strField(r, ['avatar', 'Avatar', 'portrait', 'Portrait']),
    clock: numField(r, ['clock', 'Clock']),
  };
}

/**
 * 引用快照里的缩略图相对路径（21ah-2）：原消息是图片/贴纸才有，其余空串。
 * 气泡内嵌引用块与 Composer 预览条共用——有图就显缩略图，不再只写「[图片]」。
 * （放这边而不是 replyModel：那边只 type 依赖本模块，不能反向值导入。）
 */
export function replyThumbPath(reply: ReplyRef): string {
  if (reply.msgType !== MsgType.Image || reply.srcMsg === '') return '';
  return parseImageEnvelope(reply.srcMsg).url;
}

export function parseMessageContent(msgType: number, msg: string): MessageContent {
  if (msgType === MsgType.Text) {
    const env = parseTextEnvelope(msg);
    return {
      kind: 'text',
      text: env.content,
      mentionType: env.mentionType,
      mentionTargets: env.mentionTargets,
      clock: env.clock,
    };
  }
  if (msgType === MsgType.Image) return parseImageEnvelope(msg);
  if (msgType === MsgType.Voice) return parseVoiceEnvelope(msg);
  if (msgType === MsgType.Video) return parseVideoEnvelope(msg);
  if (msgType === MsgType.File) return parseFileEnvelope(msg);
  if (msgType === MsgType.Card) return parseContactCardEnvelope(msg);
  const label = messagePreview(msgType, msg);
  return { kind: 'placeholder', label: label !== '' ? label : '[暂不支持的消息]' };
}

// ---------------------------------------------------------------------------
// 时间线条目（虚拟列表的扁平行；run 以 runStart/runEnd 标志表达）
// ---------------------------------------------------------------------------

export interface MessageItemVM {
  readonly type: 'message';
  /** 虚拟列表稳定 key：服务端行 msg:<id>；乐观消息 msg:c:<clientId>（状态变化不换） */
  readonly key: string;
  /** 乐观消息=0（尚无服务端 id） */
  readonly id: number;
  readonly uuid: string;
  readonly msgType: number;
  readonly outgoing: boolean;
  readonly senderId: number;
  readonly nickname: string;
  readonly avatar: string;
  /** 0=成员 1=管理员 2=群主（仅群聊有意义） */
  readonly senderRole: number;
  /** 发送者 VIP 等级（21g-2）；0=非 VIP。旧行缺字段按 0 兜底 */
  readonly vipLevel: number;
  /** 发送者靓号（21g-4）；空串=无。旧行缺字段按空串兜底 */
  readonly premiumNo: string;
  readonly edited: boolean;
  /** 置顶标记（14.10；StoredMessage.pin≠0。乐观行恒 false——回显前无从置顶） */
  readonly pinned: boolean;
  /**
   * 阅后即焚（14.13c；isSecretMessage 口径：envelope clock>0 或行上已有
   * deleteTime）：meta 火焰标记 + 转发/多选排除。乐观行按 envelope clock 判定
   * （发送即标，回显不变脸）。
   */
  readonly secret: boolean;
  /**
   * 端到端加密（14.14b；行 isEncrypted≠0）：meta 🔒 标记 + 禁编辑/转发/多选
   * （menuModel/selectionModel/forwardModel 同口径）。解密成功的行保留此标记
   * （msg 已是明文，14.14a 装饰）；乐观行按会话 encryptionOn 判（发送即标，
   * 回显不变脸——secret 同策）。
   */
  readonly encrypted: boolean;
  /**
   * 本机无密钥/密钥不匹配（14.14a 装饰 decryptFailed=1，msg 已置空）：content
   * 强制「需要密钥」占位（不渲染密文也不出复制/另存为等内容动作）。
   */
  readonly decryptFailed: boolean;
  /** 0=createdAt 不可解析 */
  readonly tsMs: number;
  /** HH:mm（不依赖 now，构建时算定） */
  readonly timeLabel: string;
  readonly content: MessageContent;
  readonly runStart: boolean;
  readonly runEnd: boolean;
  /** 群聊对方消息 run 首条：气泡上方显示昵称 */
  readonly showNickname: boolean;
  /** 群聊对方消息 run 末条：旁侧显示头像（旧版私聊双方与群聊自己都不显示头像） */
  readonly showAvatar: boolean;
  /** 乐观消息状态（undefined=服务端行）。角标/重试交互归 10d UI */
  readonly sendState?: OutboxStatus;
  /**
   * 失败原因原文（21f，仅 sendState='failed' 且服务端给了文案时有值）。
   * undefined = 无服务端文案（WS 未就绪/超时/上传失败），UI 回落「发送失败」。
   */
  readonly sendError?: string;
  /** 引用回复（14.6，来自 ext）：气泡内嵌引用块；undefined=非引用消息 */
  readonly replyTo?: ReplyRef;
}

export interface DateItemVM {
  readonly type: 'date';
  /** 按日历日稳定（翻页 prepend 不换 key，虚拟列表测高缓存不失效） */
  readonly key: string;
  readonly tsMs: number;
}

/** 居中提示条。文案映射归 14.5，9e 先渲染通用占位。 */
export interface SystemItemVM {
  readonly type: 'system';
  readonly key: string;
  readonly id: number;
  readonly msgType: number;
  readonly msg: string;
  readonly ext: string;
  readonly tsMs: number;
}

export type TimelineItemVM = MessageItemVM | DateItemVM | SystemItemVM;

export interface BuildTimelineOptions {
  readonly isGroup: boolean;
  readonly myUid: number;
  /** 本会话的乐观发送条目（messagesState.outbox 切片），追加在服务端行之后 */
  readonly outbox?: readonly OutboxEntry[];
  /**
   * 会话端到端加密态（14.14b；timeline.encryptionOn）：乐观行即时标 encrypted
   * （帧是密文、展示是明文——OutboxEntry.wireMsg 分离，14.14a），回显行自带
   * isEncrypted 不变脸。缺省 false。
   */
  readonly encryptionOn?: boolean;
  /**
   * 群聊发送者显示名覆盖表（21am-7，`uid → 名字`，groupMemberName.buildSenderNameMap）。
   *
   * 消息行里的 `nickname` 是**发送时点服务端烙进去的原始昵称快照，不含群备注**
   * （WS 群发路径让 GetGroupNickname 成了死代码，见 groupMemberName 文件头），
   * 所以群聊要拿成员列表 + 好友备注现算的名字盖掉它。
   * 缺省/未命中都原样用行里的 nickname——成员列表没加载好时退化成现状，不留空名。
   */
  readonly senderNames?: ReadonlyMap<number, string>;
  /**
   * 我在本群的角色（0 成员 / 1 管理员 / 2 群主；timeline.composer.myRole，
   * 14.25a 既有通道零新增请求）。管理向提示条按它过滤——见 preview.ts
   * ADMIN_ONLY_NOTE_TYPES。缺省 0：私聊无 note 帧、群聊快照未回来时宁可少显
   * 一条（回包落定即补上），不能反过来先闪给普通成员看。
   */
  readonly myRole?: number;
}

/** outbox 条目 → 本地合成行（id=0 标记、uuid=clientId，run/日期逻辑照常参与） */
function outboxToRow(entry: OutboxEntry, myUid: number, encrypted: boolean): StoredMessage {
  return {
    conversationKey: entry.conversationKey,
    id: 0,
    userId: myUid,
    msgType: entry.msgType,
    msg: entry.msg,
    createdAt: new Date(entry.createdAtMs).toISOString(),
    nickname: '',
    avatar: '',
    ext: entry.ext,
    pin: 0,
    uuid: entry.clientId,
    isEncrypted: encrypted ? 1 : 0,
    edited: 0,
    senderRole: 0,
  };
}

export function parseMessageTimestamp(createdAt: string): number {
  const ms = Date.parse(createdAt);
  return Number.isFinite(ms) ? ms : 0;
}

/** run 归属（旧版 _isSameSenderMessageGroup）：群聊 + 同发送者 + 同日历日 */
function sameRun(
  prev: StoredMessage,
  prevTs: number,
  cur: StoredMessage,
  curTs: number,
  isGroup: boolean,
): boolean {
  if (!isGroup) return false;
  if (prev.userId !== cur.userId) return false;
  return localDayOrdinal(prevTs) === localDayOrdinal(curTs) && localDayOrdinal(curTs) !== 0;
}

/**
 * rows（升序）→ 渲染条目。O(n)，每次时间线变更整体重建——10k 行量级在
 * Worker 推送合帧（按帧批量）之下可接受；若实测成瓶颈再做增量。
 */
export function buildTimelineItems(
  rows: readonly StoredMessage[],
  opts: BuildTimelineOptions,
): TimelineItemVM[] {
  // 乐观消息合成行追加在服务端行之后（文件头口径）；状态按 uuid(=clientId) 回查
  let source = rows;
  const localStates = new Map<string, OutboxStatus>();
  /** clientId → 服务端失败原文（21f）；没有文案的条目不进表 */
  const localErrors = new Map<string, string>();
  if (opts.outbox !== undefined && opts.outbox.length > 0) {
    for (const e of opts.outbox) {
      localStates.set(e.clientId, e.status);
      if (e.errorText !== undefined && e.errorText !== '') localErrors.set(e.clientId, e.errorText);
    }
    source = [
      ...rows,
      ...opts.outbox.map((e) => outboxToRow(e, opts.myUid, opts.encryptionOn === true)),
    ];
  }

  const items: TimelineItemVM[] = [];
  let prevDay = -1;
  // 当前 run 的末条消息在 items 里的下标（遇到新 run / 非消息行时收口 runEnd）
  let openRunLast = -1;
  let prevRow: StoredMessage | null = null;
  let prevTs = 0;
  /** 上一条已输出消息行的 album_id（非相册图片/非图片=空串），相邻合并判定用 */
  let prevAlbumId = '';

  const closeRun = () => {
    if (openRunLast < 0) return;
    const last = items[openRunLast] as MessageItemVM;
    items[openRunLast] = {
      ...last,
      runEnd: true,
      showAvatar: opts.isGroup && !last.outgoing,
    };
    openRunLast = -1;
  };

  for (const row of source) {
    const spec = msgTypeSpec(row.msgType);
    if (spec.category === 'signal') continue;
    // 管理向提示条（21ah 人工指图）：普通成员整条不渲染。判在日期条之前——
    // 否则「当天只有这一条」会留下一个空的日期分隔
    if (spec.category === 'system-note' && isAdminOnlyNote(row.msgType, opts.myRole ?? 0)) continue;

    const tsMs = parseMessageTimestamp(row.createdAt);
    const day = localDayOrdinal(tsMs);
    if (day !== 0 && day !== prevDay) {
      closeRun();
      items.push({ type: 'date', key: `date:${day}`, tsMs });
      prevDay = day;
    }

    if (spec.category === 'system-note') {
      closeRun();
      items.push({
        type: 'system',
        key: `sys:${row.id}`,
        id: row.id,
        msgType: row.msgType,
        msg: row.msg,
        ext: row.ext,
        tsMs,
      });
      prevRow = null;
      prevAlbumId = '';
      continue;
    }

    // 相册合并（14.1）：相邻 + 同发送者 + 同非空 album_id 的图片并入上一条目。
    // 日期条/系统条会把「上一条目」顶成非 message，天然切断；合并不动 run 状态
    // （openRunLast 仍指向该条目，runEnd 照常收口）。刻意不比 isGroup——旧版
    // 只在群聊 run 内合并致私聊相册散开，这里两种会话同口径（日志有记）。
    // 解密失败的图片行 msg 已空（14.14a 装饰），不进相册合并——各自渲染
    // 「需要密钥」占位，免得合成一册空白网格
    const albumId =
      row.msgType === MsgType.Image && row.decryptFailed !== 1 ? parseAlbumId(row.ext) : '';
    if (albumId !== '' && albumId === prevAlbumId && prevRow !== null && prevRow.userId === row.userId) {
      const last = items[items.length - 1];
      if (last !== undefined && last.type === 'message') {
        const img = parseImageEnvelope(row.msg);
        const tileState = row.id === 0 ? localStates.get(row.uuid) : undefined;
        const tile: AlbumImageVM = {
          key: row.id === 0 ? `msg:c:${row.uuid}` : `msg:${row.id}`,
          id: row.id,
          uuid: row.uuid,
          url: img.url,
          width: img.width,
          height: img.height,
          pinned: row.pin !== 0,
          ...(tileState !== undefined ? { sendState: tileState } : {}),
        };
        let content: MessageContent | null = null;
        if (last.content.kind === 'album') {
          content = { kind: 'album', albumId, images: [...last.content.images, tile] };
        } else if (last.content.kind === 'image') {
          const first: AlbumImageVM = {
            key: last.key,
            id: last.id,
            uuid: last.uuid,
            url: last.content.url,
            width: last.content.width,
            height: last.content.height,
            pinned: last.pinned,
            ...(last.sendState !== undefined ? { sendState: last.sendState } : {}),
          };
          content = { kind: 'album', albumId, images: [first, tile] };
        }
        if (content !== null) {
          items[items.length - 1] = { ...last, content };
          prevRow = row;
          prevTs = tsMs;
          continue;
        }
      }
    }

    const outgoing = row.userId === opts.myUid;
    const joinsRun = prevRow !== null && sameRun(prevRow, prevTs, row, tsMs, opts.isGroup);
    if (!joinsRun) closeRun();
    const runStart = !joinsRun;
    const sendState = row.id === 0 ? localStates.get(row.uuid) : undefined;
    const sendError = row.id === 0 ? localErrors.get(row.uuid) : undefined;
    // 引用回复（14.6）：旧版只在文本发送时写 reply ext，但解析对所有气泡行生效
    // （与旧版 parseReply 调用点同级——不按 msgType 预筛）
    const replyTo = row.ext !== '' ? parseReplyExt(row.ext) : null;
    items.push({
      type: 'message',
      key: row.id === 0 ? `msg:c:${row.uuid}` : `msg:${row.id}`,
      id: row.id,
      uuid: row.uuid,
      msgType: row.msgType,
      outgoing,
      senderId: row.userId,
      // 群备注/好友备注优先（21am-7）；表里没有就用行里的昵称快照
      nickname: opts.senderNames?.get(row.userId) ?? row.nickname,
      avatar: row.avatar,
      senderRole: row.senderRole,
      vipLevel: row.vipLevel ?? 0,
      premiumNo: row.premiumNo ?? '',
      edited: row.edited !== 0,
      pinned: row.pin !== 0,
      secret: isSecretMessage(row.msg, row.deleteTime),
      encrypted: row.isEncrypted !== 0,
      decryptFailed: row.decryptFailed === 1,
      tsMs,
      timeLabel: formatBubbleTime(tsMs),
      content:
        row.decryptFailed === 1
          ? { kind: 'placeholder', label: '需要密钥' }
          : parseMessageContent(row.msgType, row.msg),
      runStart,
      runEnd: false, // 收口时回填
      showNickname: opts.isGroup && !outgoing && runStart,
      showAvatar: false, // 收口时回填（runEnd 且对方消息）
      ...(sendState !== undefined ? { sendState } : {}),
      ...(sendError !== undefined ? { sendError } : {}),
      ...(replyTo !== null ? { replyTo } : {}),
    });
    openRunLast = items.length - 1;
    prevRow = row;
    prevTs = tsMs;
    prevAlbumId = albumId;
  }
  closeRun();
  return items;
}

/**
 * 相册里单张 → 单图消息视图（14.1）：菜单/预览按张操作时用，动作口径与独立
 * 图片消息完全一致（readyMenuActions 收 contentKind='image'、删除按张 id、
 * 重试/删除按张 uuid）。昵称/头像标志清零——张不是独立渲染行。
 */
export function albumTileToItem(item: MessageItemVM, img: AlbumImageVM): MessageItemVM {
  return {
    type: 'message',
    key: img.key,
    id: img.id,
    uuid: img.uuid,
    msgType: MsgType.Image,
    outgoing: item.outgoing,
    senderId: item.senderId,
    nickname: item.nickname,
    avatar: item.avatar,
    senderRole: item.senderRole,
    vipLevel: item.vipLevel,
    premiumNo: item.premiumNo,
    edited: false,
    pinned: img.pinned,
    secret: item.secret,
    encrypted: item.encrypted,
    decryptFailed: false, // 解密失败的行不进相册合并，tile 必来自解密成功的册
    tsMs: item.tsMs,
    timeLabel: item.timeLabel,
    content: {
      kind: 'image',
      url: img.url,
      width: img.width,
      height: img.height,
      clock: 0,
      stickerPayload: false,
      stickerPackId: 0,
    },
    runStart: item.runStart,
    runEnd: item.runEnd,
    showNickname: false,
    showAvatar: false,
    ...(img.sendState !== undefined ? { sendState: img.sendState } : {}),
  };
}
